import { useRef, useEffect, useState, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import debounce from 'lodash/debounce';
import { FreeMode, Scrollbar } from 'swiper/modules';
import InspirationSmallHeaderBlock from '@features/cms/components/inspirations/InspirationSmallHeaderBlock/InspirationSmallHeaderBlock';
import ProductGalleryBlockSlide from '@features/cms/components/collections/ProductGalleryBlock/ProductGalleryBlockSlide/ProductGalleryBlockSlide';
import { useResponsive } from '@features/responsive';
import SlideControl from '@components/SlideControl/SlideControl';
import ProductGalleryBlockModalButton from '@features/cms/components/collections/ProductGalleryBlock/ProductGalleryBlockModalButton/ProductGalleryBlockModalButton';
import ProductGalleryBlockModal from '@features/cms/components/collections/ProductGalleryBlock/ProductGalleryBlockModal/ProductGalleryBlockModal';
import useBodyOverflow from '@hooks/useBodyOverflow';
import styles from '@features/cms/components/collections/ProductGalleryBlock/ProductGalleryBlockContent/ProductGalleryBlockContent.styles';
import { IProductGalleryBlock, TProductGallerySlide } from '@features/cms/typescript';
import { IParsedImage } from '@components/typescript';
import ModalRootPortal from '@components/ModalRootPortal/ModalRootPortal';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/scrollbar';
type PropsType = {
  data: IProductGalleryBlock<IParsedImage>;
  isFirst: boolean;
};
const ProductGalleryBlockContent = ({
  data,
  isFirst
}: PropsType) => {
  const {
    slides
  } = data;
  const swiperInstance = useRef<SwiperClass>(null);
  const {
    isWiderThanTablet
  } = useResponsive();
  const updateSwiper = debounce(() => swiperInstance.current?.update(), 500);
  const [currentModalSlide, setCurrentModalSlide] = useState<TProductGallerySlide<IParsedImage> | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [shouldRenderArrows, SetShouldRenderArrows] = useState({
    left: false,
    right: true
  });
  useBodyOverflow(isModalOpen, 'productGalleryBlock', false, true);
  useEffect(() => {
    if (typeof window !== 'undefined') window.addEventListener('resize', updateSwiper);
    return () => window.removeEventListener('resize', updateSwiper);
  }, [updateSwiper]);
  const updateRenderArrows = () => {
    SetShouldRenderArrows({
      left: !swiperInstance.current?.isBeginning,
      right: !swiperInstance.current?.isEnd
    });
  };
  const onSlideChange = () => {
    updateRenderArrows();
  };
  const onControlClick = (dir: 'next' | 'prev') => {
    if (dir === 'prev') swiperInstance.current?.slidePrev();
    if (dir === 'next') swiperInstance.current?.slideNext();
    updateRenderArrows();
  };
  const onSlideClick = (slide: TProductGallerySlide<IParsedImage>) => {
    setModalOpen(true);
    setCurrentModalSlide(slide);
  };
  const onCloseModalButton = () => {
    setModalOpen(false);
    setCurrentModalSlide(null);
  };
  const renderSlide = (slide: TProductGallerySlide<IParsedImage>) => {
    const haveProducts = slide.products.length > 0;
    return <SwiperSlide key={slide.itemId} onClick={haveProducts ? () => onSlideClick(slide) : () => null}>
        <div sx={styles.slideContainer}>
          <div sx={styles.sliderImgContainer(haveProducts)} className={haveProducts ? 'photo_with_products' : ''}>
            <ProductGalleryBlockSlide image={slide.image} alt={slide.products[0]?.name || data.title || ''} />
          </div>
          {haveProducts && <ProductGalleryBlockModalButton />}
        </div>
      </SwiperSlide>;
  };
  const renderedSlides = useMemo(() => slides.map(slide => renderSlide(slide)), [slides]);
  return <>
      {data.title && <InspirationSmallHeaderBlock data={{
      title: data.title
    }} isFirst={isFirst} />}
      <div sx={styles.sliderContainer}>
        <Swiper sx={styles.scrollbar(data.scrollbar)} spaceBetween={isWiderThanTablet ? 24 : 4} slidesPerView="auto" freeMode modules={[Scrollbar, FreeMode]} scrollbar={{
        draggable: true
      }} onSwiper={swiper => {
        swiperInstance.current = swiper;
      }} data-cy="Swiper" onActiveIndexChange={onSlideChange}>
          {renderedSlides}
        </Swiper>
      </div>
      {shouldRenderArrows.right && <SlideControl direction="next" onClick={onControlClick} withScrollbar />}
      {shouldRenderArrows.left && <SlideControl direction="prev" onClick={onControlClick} withScrollbar />}
      {currentModalSlide && <ModalRootPortal>
          <ProductGalleryBlockModal isActive={isModalOpen} slide={currentModalSlide} onClose={onCloseModalButton} />
        </ModalRootPortal>}
    </>;
};
export default ProductGalleryBlockContent;